import { useEffect, useRef } from 'react';
import { useSignalR } from '../providers/SignalR';

/** @type {Record<string, string>} */
const ENV_MAP = {
  development: 'dev',
  staging: 'staging',
  production: 'production',
  waystone: 'waystone',
  demo: 'demo',
  custom_production: 'custom-report'
};

/**
 * Hook for listening to SignalR messages with automatic environment filtering.
 * Messages from different environments (dev, staging, production, demo, custom-report)
 * are automatically filtered out.
 * @param {string} method
 * @param {(...args: unknown[]) => void} callback
 * @returns {void}
 */
export const useSignalRListener = (method, callback) => {
  const { service, isConnected } = useSignalR();
  const callBackRef = useRef(callback);

  useEffect(() => {
    callBackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (isConnected && service) {
      const handler = (...args) => {
        const data = args[0];

        const currentEnv = import.meta.env.VITE_APP_ENV;
        const backendEnv = data?.environment;

        if (!backendEnv) {
          if (import.meta.env.DEV) {
            console.log(
              `[SignalR ${method}] No environment in payload - processing message without environment filter`,
              { currentEnv }
            );
          }
          callBackRef.current(...args);
          return;
        }

        const mappedBackendEnv = ENV_MAP[backendEnv] || backendEnv;

        if (mappedBackendEnv === currentEnv) {
          if (import.meta.env.DEV) {
            console.log(`[SignalR ${method}] Environment match - processing message`, {
              backendEnv,
              currentEnv
            });
          }
          callBackRef.current(...args);
        } else {
          if (import.meta.env.DEV) {
            console.log(`[SignalR ${method}] Environment mismatch - ignoring message`, {
              backendEnv,
              mappedBackendEnv,
              currentEnv
            });
          }
        }
      };

      service.on(method, handler);

      return () => {
        service.off(method, handler);
      };
    }
  }, [isConnected, service, method]);
};
